import { getRuntimeConfig } from './runtimeConfig'

let apiBase = import.meta.env.VITE_API_URL || ''

let accessToken: string | null = null

export function setApiToken(token: string | null) {
  accessToken = token
}

export interface PublicShareInfo {
  title: string
  file_name: string
  label?: string
  enabled: boolean
  updated_at: string
}

export interface PublicFileLink {
  id: string
  file_id: string
  user_id: string
  org_id: string
  token: string
  label?: string
  enabled: boolean
  last_used_at?: string
  created_at: string
  updated_at: string
}

export interface PublicSharedFileItem {
  file_id: string
  file?: FileRecord
  links: PublicFileLink[]
  enabled_count: number
}

export interface PublicFileAccessLog {
  id: string
  link_id: string
  file_id: string
  user_id: string
  org_id: string
  ip_address?: string
  user_agent?: string
  created_at: string
}

export function setApiBaseUrl(baseUrl: string) {
  apiBase = baseUrl
}

function buildHeaders(options: RequestInit = {}, orgId?: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }

  if (orgId) {
    headers['X-Org-ID'] = orgId
  }

  return headers
}

function buildApiUrl(endpoint: string): string {
  const cfg = getRuntimeConfig()
  const base = (cfg?.apiUrl || apiBase || '').replace(/\/+$/, '')
  if (!base) return `/api/v1${endpoint}`

  if (base.endsWith('/api/v1')) {
    return `${base}${endpoint}`
  }

  return `${base}/api/v1${endpoint}`
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  orgId?: string
): Promise<T> {
  const url = buildApiUrl(endpoint)
  const headers = buildHeaders(options, orgId)

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include', // Send cookies for session auth
  })

  if (!response.ok) {
    // Handle 401 Unauthorized - clear token and surface error (no redirect here to avoid loops)
    if (response.status === 401) {
      accessToken = null
      throw new Error('Session expired. Please log in again.')
    }
    
    const error = await response.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(error.error || 'Request failed')
  }

  return response.json()
}

async function uploadMultipartWithProgress<T>(
  endpoint: string,
  formData: FormData,
  orgId: string,
  onProgress?: (percent: number) => void
): Promise<T> {
  const url = buildApiUrl(endpoint)

  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url)
    xhr.withCredentials = true // Send cookies for session auth

    if (accessToken) {
      xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`)
    }
    xhr.setRequestHeader('X-Org-ID', orgId)

    xhr.upload.onprogress = (evt) => {
      if (!evt.lengthComputable) return
      const percent = Math.round((evt.loaded / evt.total) * 100)
      onProgress?.(percent)
    }

    xhr.onload = () => {
      const ok = xhr.status >= 200 && xhr.status < 300
      let parsed: any = null
      try {
        parsed = xhr.responseText ? JSON.parse(xhr.responseText) : null
      } catch {
        parsed = null
      }

      if (!ok) {
        // Handle 401 Unauthorized - token expired or invalid
        if (xhr.status === 401) {
          console.log('[Freehold][API] 401 Unauthorized on upload')
          accessToken = null
          throw new Error('Session expired. Please log in again.')
        }
        
        const msg = parsed?.error || 'Upload failed'
        reject(new Error(msg))
        return
      }

      resolve(parsed as T)
    }

    xhr.onerror = () => {
      reject(new Error('Upload failed'))
    }

    xhr.send(formData)
  })
}

async function uploadToPresignedUrlWithProgress(
  uploadUrl: string,
  file: Blob,
  contentType: string,
  onProgress?: (percent: number) => void,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', uploadUrl)

    if (contentType) {
      xhr.setRequestHeader('Content-Type', contentType)
    }

    xhr.upload.onprogress = (evt) => {
      if (!evt.lengthComputable) return
      const percent = Math.round((evt.loaded / evt.total) * 100)
      onProgress?.(percent)
    }

    xhr.onload = () => {
      const ok = xhr.status >= 200 && xhr.status < 300
      if (!ok) {
        reject(new Error(`Upload failed (${xhr.status})`))
        return
      }
      resolve()
    }

    xhr.onerror = () => {
      reject(new Error('Upload failed'))
    }

    xhr.send(file)
  })
}

export const api = {
  me: {
    get: () => request<{ user: User; orgs: Org[]; my_files_org_id?: string }>('/me'),
    update: (data: Partial<User>) => request<{ user: User }>('/me', { method: 'PATCH', body: JSON.stringify(data) }),
    publicDrop: {
      list: () => request<{ links: PublicDropLink[] }>('/me/public-drop-links'),
      create: (data: { label?: string }) =>
        request<{ link: PublicDropLink }>('/me/public-drop-links', { method: 'POST', body: JSON.stringify(data) }),
      update: (linkId: string, data: { label?: string | null; enabled?: boolean }) =>
        request<{ link: PublicDropLink }>(`/me/public-drop-links/${linkId}`, { method: 'PATCH', body: JSON.stringify(data) }),
      regenerate: (linkId: string) =>
        request<{ link: PublicDropLink }>(`/me/public-drop-links/${linkId}/regenerate`, { method: 'POST' }),
    },
  },

  publicShare: {
    info: async (token: string) => {
      const url = buildApiUrl(`/public/share/${encodeURIComponent(token)}`)
      const response = await fetch(url, { method: 'GET' })
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Link not found' }))
        throw new Error(error.error || 'Link not found')
      }
      return response.json() as Promise<PublicShareInfo>
    },
    downloadUrl: (token: string) => buildApiUrl(`/public/share/${encodeURIComponent(token)}/download`),
  },

  publicDrop: {
    info: async (token: string) => {
      const url = buildApiUrl(`/public/drop/${encodeURIComponent(token)}`)
      const response = await fetch(url, { method: 'GET' })
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Link not found' }))
        throw new Error(error.error || 'Link not found')
      }
      return response.json() as Promise<PublicDropInfo>
    },
    upload: async (token: string, files: File[]) => {
      const form = new FormData()
      for (const f of files) {
        form.append('files', f, f.name)
      }
      const url = buildApiUrl(`/public/drop/${encodeURIComponent(token)}/upload`)
      const response = await fetch(url, {
        method: 'POST',
        body: form,
      })
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Upload failed' }))
        throw new Error(error.error || 'Upload failed')
      }
      return response.json() as Promise<{ success: boolean; count: number }>
    },
  },

  admin: {
    users: {
      list: () => request<{ users: User[] }>('/admin/users'),
      setAdmin: (userId: string, isAdmin: boolean) =>
        request<{ user: User }>(`/admin/users/${userId}`, { method: 'PATCH', body: JSON.stringify({ is_admin: isAdmin }) }),
    },
    settings: {
      getPublicSharing: () => request<{ allow_regular_users_public_file_links: boolean }>('/admin/settings/public-sharing'),
      updatePublicSharing: (allow: boolean) =>
        request<{ allow_regular_users_public_file_links: boolean }>('/admin/settings/public-sharing', {
          method: 'PATCH',
          body: JSON.stringify({ allow_regular_users_public_file_links: allow }),
        }),
    },
    logs: {
      get: (limit?: number) => request<{ logs: { timestamp: string; level: string; source: string; message: string }[] }>(`/admin/logs?limit=${limit || 100}`),
    },
  },

  orgs: {
    list: () => request<{ orgs: Org[] }>('/orgs'),
    create: (data: { name: string; slug: string }) => request<{ org: Org }>('/orgs', { method: 'POST', body: JSON.stringify(data) }),
    get: (orgId: string) => request<{ org: Org }>('/org', {}, orgId),
    members: {
      list: (orgId: string) => request<{ members: OrgMember[] }>('/org/members', {}, orgId),
      add: (orgId: string, data: { email?: string; user_id?: string; role?: string }) =>
        request<{ member: OrgMember }>('/org/members', { method: 'POST', body: JSON.stringify(data) }, orgId),
      update: (orgId: string, userId: string, data: { role: string }) =>
        request<{ member: OrgMember }>(`/org/members/${userId}`, { method: 'PATCH', body: JSON.stringify(data) }, orgId),
      remove: (orgId: string, userId: string) =>
        request<{ success: boolean }>(`/org/members/${userId}`, { method: 'DELETE' }, orgId),
    },
  },

  teams: {
    list: (orgId: string) => request<{ teams: Team[] }>('/teams', {}, orgId),
    my: (orgId: string) => request<{ teams: Team[] }>('/teams/my', {}, orgId),
    get: (orgId: string, teamId: string) => request<{ team: Team }>(`/teams/${teamId}`, {}, orgId),
    create: (orgId: string, data: { name: string; slug: string; description?: string }) =>
      request<{ team: Team }>('/teams', { method: 'POST', body: JSON.stringify(data) }, orgId),
    update: (orgId: string, teamId: string, data: Partial<Team>) =>
      request<{ team: Team }>(`/teams/${teamId}`, { method: 'PATCH', body: JSON.stringify(data) }, orgId),
    delete: (orgId: string, teamId: string) =>
      request<{ success: boolean }>(`/teams/${teamId}`, { method: 'DELETE' }, orgId),
    addMember: (orgId: string, teamId: string, data: { user_id: string; role?: string }) =>
      request<{ member: TeamMember }>(`/teams/${teamId}/members`, { method: 'POST', body: JSON.stringify(data) }, orgId),
    removeMember: (orgId: string, teamId: string, userId: string) =>
      request<{ success: boolean }>(`/teams/${teamId}/members/${userId}`, { method: 'DELETE' }, orgId),
  },

  folders: {
    root: (orgId: string, ownerType?: string, ownerId?: string) => {
      const params = new URLSearchParams()
      if (ownerType) params.set('owner_type', ownerType)
      if (ownerId) params.set('owner_id', ownerId)
      return request<{ folder: Folder }>(`/folders/root?${params}`, {}, orgId)
    },
    get: (orgId: string, folderId: string) => request<{ folder: Folder }>(`/folders/${folderId}`, {}, orgId),
    contents: (orgId: string, folderId: string) =>
      request<{ folder: Folder; folders: Folder[]; files: FileWithVersion[]; path: Folder[] }>(`/folders/${folderId}/contents`, {}, orgId),
    path: (orgId: string, folderId: string) => request<{ path: Folder[] }>(`/folders/${folderId}/path`, {}, orgId),
    create: (orgId: string, data: { parent_id?: string; name: string; owner_type?: string; owner_id?: string }) =>
      request<{ folder: Folder }>('/folders', { method: 'POST', body: JSON.stringify(data) }, orgId),
    update: (orgId: string, folderId: string, data: { name: string }) =>
      request<{ folder: Folder }>(`/folders/${folderId}`, { method: 'PATCH', body: JSON.stringify(data) }, orgId),
    delete: (orgId: string, folderId: string) =>
      request<{ success: boolean }>(`/folders/${folderId}`, { method: 'DELETE' }, orgId),
    move: (orgId: string, folderId: string, destinationId: string) =>
      request<{ success: boolean }>(`/folders/${folderId}/move`, { method: 'POST', body: JSON.stringify({ destination_id: destinationId }) }, orgId),
  },

  files: {
    get: (orgId: string, fileId: string) => request<{ file: FileWithVersion; tags: Tag[] }>(`/files/${fileId}`, {}, orgId),
    update: (orgId: string, fileId: string, data: { name?: string; description?: string }) =>
      request<{ file: FileRecord }>(`/files/${fileId}`, { method: 'PATCH', body: JSON.stringify(data) }, orgId),
    delete: (orgId: string, fileId: string) =>
      request<{ success: boolean }>(`/files/${fileId}`, { method: 'DELETE' }, orgId),
    move: (orgId: string, fileId: string, destinationId: string) =>
      request<{ success: boolean }>(`/files/${fileId}/move`, { method: 'POST', body: JSON.stringify({ destination_id: destinationId }) }, orgId),
    downloadBlob: async (orgId: string, fileId: string, opts?: { inline?: boolean; compressed?: boolean }) => {
      const params = new URLSearchParams()
      if (opts?.inline) params.set('inline', 'true')
      if (opts?.compressed) params.set('compressed', 'true')
      if (orgId) params.set('org_id', orgId)
      const endpoint = `/files/${fileId}/download${params.toString() ? `?${params}` : ''}`

      const url = buildApiUrl(endpoint)
      const response = await fetch(url, {
        method: 'GET',
        headers: buildHeaders({}, orgId),
        credentials: 'include',
      })

      if (!response.ok) {
        // Handle 401 Unauthorized - surface error without redirect to avoid loops
        if (response.status === 401) {
          console.warn('[Freehold][API] 401 Unauthorized on download')
          accessToken = null
          throw new Error('Session expired. Please log in again.')
        }
        const error = await response.json().catch(() => ({ error: 'Download failed' }))
        throw new Error(error.error || 'Download failed')
      }
      return response.blob()
    },
    thumbnail: {
      /** Fetch thumbnail bytes directly – the response carries 90-day Cache-Control + ETag headers
       *  so the browser caches it and never re-fetches the same version. */
      getBlob: async (
        orgId: string,
        fileId: string,
        size: 'small' | 'large' = 'small'
      ): Promise<Blob | null> => {
        const endpoint = `/files/${fileId}/thumbnail?size=${size}`
        const url = buildApiUrl(endpoint)
        const response = await fetch(url, {
          method: 'GET',
          headers: buildHeaders({}, orgId),
          credentials: 'include',
        })

        if (response.status === 404) {
          return null
        }

        if (!response.ok) {
          if (response.status === 401) {
            console.warn('[Freehold][API] 401 Unauthorized on thumbnail')
            accessToken = null
            throw new Error('Session expired. Please log in again.')
          }
          return null
        }

        return response.blob()
      },
      getUrl: async (
        orgId: string,
        fileId: string,
        size: 'small' | 'large' = 'small'
      ): Promise<{ url: string; expires_at?: string } | null> => {
        const blob = await api.files.thumbnail.getBlob(orgId, fileId, size)
        if (!blob) return null
        const objectUrl = URL.createObjectURL(blob)
        return { url: objectUrl, expires_at: undefined }
      },
      upload: async (
        orgId: string,
        fileId: string,
        blob: Blob,
        contentType?: string,
        size: 'small' | 'large' = 'small'
      ) => {
        const endpoint = `/files/${fileId}/thumbnail?size=${size}`
        const url = buildApiUrl(endpoint)
        const response = await fetch(url, {
          method: 'POST',
          headers: buildHeaders(
            {
              headers: {
                'Content-Type': contentType || blob.type || 'image/png',
              },
            },
            orgId
          ),
          credentials: 'include',
          body: blob,
        })

        if (!response.ok) {
          if (response.status === 401) {
            console.warn('[Freehold][API] 401 Unauthorized on thumbnail upload')
            accessToken = null
            throw new Error('Session expired. Please log in again.')
          }
          const error = await response.json().catch(() => ({ error: 'Thumbnail upload failed' }))
          throw new Error(error.error || 'Thumbnail upload failed')
        }

        return response.json() as Promise<{ success: boolean; url?: string; expires_at?: string }>
      },
    },
    search: (orgId: string, query: string) => request<{ files: FileRecord[] }>(`/files/search?q=${encodeURIComponent(query)}`, {}, orgId),
    upload: {
      initiate: (orgId: string, data: { folder_id: string; filename: string; content_type: string; size: number; description?: string }) =>
        request<{ file_id: string; version_id: string; upload_url: string; storage_key: string; expires_at: string }>(
          '/files/upload/initiate', { method: 'POST', body: JSON.stringify(data) }, orgId
        ),
      finalize: (orgId: string, data: { file_id: string; version_id: string; checksum?: string }) =>
        request<{ success: boolean; file: FileRecord; version: FileVersion }>(
          '/files/upload/finalize', { method: 'POST', body: JSON.stringify(data) }, orgId
        ),
      direct: (
        orgId: string,
        data: { folder_id: string; file: Blob; filename: string },
        onProgress?: (percent: number) => void
      ) => {
        const form = new FormData()
        form.set('folder_id', data.folder_id)
        form.set('file', data.file, data.filename)
        return uploadMultipartWithProgress<{ success: boolean; file: FileRecord; version: FileVersion }>(
          '/files/upload',
          form,
          orgId,
          onProgress
        )
      },
      presigned: async (
        orgId: string,
        data: { folder_id: string; file: Blob; filename: string; description?: string },
        onProgress?: (percent: number) => void
      ) => {
        const contentType = (data.file as any)?.type || 'application/octet-stream'
        const size = (data.file as any)?.size || 0

        const initiated = await request<{ file_id: string; version_id: string; upload_url: string; storage_key: string; expires_at: string; post_url?: string; post_fields?: Record<string, string> }>(
          '/files/upload/initiate',
          {
            method: 'POST',
            body: JSON.stringify({
              folder_id: data.folder_id,
              filename: data.filename,
              content_type: contentType,
              size,
              description: data.description,
            }),
          },
          orgId
        )

        await uploadToPresignedUrlWithProgress(initiated.upload_url, data.file, contentType, onProgress)

        return request<{ success: boolean; file: FileRecord; version: FileVersion }>(
          '/files/upload/finalize',
          {
            method: 'POST',
            body: JSON.stringify({
              file_id: initiated.file_id,
              version_id: initiated.version_id,
            }),
          },
          orgId
        )
      },
    },
    versions: {
      list: (orgId: string, fileId: string) => request<{ versions: FileVersion[] }>(`/files/${fileId}/versions`, {}, orgId),
      upload: (orgId: string, fileId: string, data: { content_type: string; size: number }) =>
        request<{ version_id: string; upload_url: string; storage_key: string; expires_at: string }>(
          `/files/${fileId}/versions`, { method: 'POST', body: JSON.stringify(data) }, orgId
        ),
      restore: (orgId: string, fileId: string, versionId: string) =>
        request<{ success: boolean }>(`/files/${fileId}/versions/${versionId}/restore`, { method: 'POST' }, orgId),
    },
    tags: {
      list: (orgId: string, fileId: string) => request<{ tags: Tag[] }>(`/files/${fileId}/tags`, {}, orgId),
      add: (orgId: string, fileId: string, data: { tag_id?: string; tag_name?: string }) =>
        request<{ success: boolean }>(`/files/${fileId}/tags`, { method: 'POST', body: JSON.stringify(data) }, orgId),
      remove: (orgId: string, fileId: string, tagId: string) =>
        request<{ success: boolean }>(`/files/${fileId}/tags/${tagId}`, { method: 'DELETE' }, orgId),
    },

		favorite: {
			add: (orgId: string, fileId: string) =>
				request<{ success: boolean }>(`/files/${fileId}/favorite`, { method: 'POST' }, orgId),
			remove: (orgId: string, fileId: string) =>
				request<{ success: boolean }>(`/files/${fileId}/favorite`, { method: 'DELETE' }, orgId),
		},
  },

	favorites: {
		list: (orgId: string) => request<{ files: FileWithVersion[] }>('/favorites', {}, orgId),
	},

  tags: {
    list: (orgId: string) => request<{ tags: Tag[] }>('/tags', {}, orgId),
    create: (orgId: string, data: { name: string; color?: string }) =>
      request<{ tag: Tag }>('/tags', { method: 'POST', body: JSON.stringify(data) }, orgId),
    update: (orgId: string, tagId: string, data: { name?: string; color?: string }) =>
      request<{ tag: Tag }>(`/tags/${tagId}`, { method: 'PATCH', body: JSON.stringify(data) }, orgId),
    delete: (orgId: string, tagId: string) =>
      request<{ success: boolean }>(`/tags/${tagId}`, { method: 'DELETE' }, orgId),
    files: (orgId: string, tagId: string) => request<{ files: FileRecord[] }>(`/tags/${tagId}/files`, {}, orgId),
  },

  shares: {
    create: (orgId: string, data: { file_id?: string; folder_id?: string; target_user_id?: string; target_team_id?: string; permission: SharePermission }) =>
      request<{ share: Share }>('/shares', { method: 'POST', body: JSON.stringify(data) }, orgId),
    listForFile: (orgId: string, fileId: string) =>
      request<{ shares: ShareWithDetails[] }>(`/files/${fileId}/shares`, {}, orgId),
    listForFolder: (orgId: string, folderId: string) =>
      request<{ shares: ShareWithDetails[] }>(`/folders/${folderId}/shares`, {}, orgId),
    getFileStatus: (orgId: string, fileId: string) =>
      request<ShareStatus>(`/files/${fileId}/share-status`, {}, orgId),
    listSharedWithMe: (orgId: string) =>
      request<{ items: SharedItem[]; dropped?: DroppedItem[] }>('/shares/with-me', {}, orgId),
    update: (orgId: string, shareId: string, data: { permission: SharePermission }) =>
      request<{ share: Share }>(`/shares/${shareId}`, { method: 'PATCH', body: JSON.stringify(data) }, orgId),
    delete: (orgId: string, shareId: string) =>
      request<{ ok: boolean }>(`/shares/${shareId}`, { method: 'DELETE' }, orgId),

    listPublicLinksForFile: (orgId: string, fileId: string) =>
      request<{ links: PublicFileLink[] }>(`/files/${fileId}/public-links`, {}, orgId),
    createPublicLinkForFile: (orgId: string, fileId: string, data: { label?: string }) =>
      request<{ link: PublicFileLink }>(`/files/${fileId}/public-links`, { method: 'POST', body: JSON.stringify(data) }, orgId),
    updatePublicLinkForFile: (orgId: string, fileId: string, linkId: string, data: { label?: string | null; enabled?: boolean }) =>
      request<{ link: PublicFileLink }>(`/files/${fileId}/public-links/${linkId}`, { method: 'PATCH', body: JSON.stringify(data) }, orgId),
    regeneratePublicLinkForFile: (orgId: string, fileId: string, linkId: string) =>
      request<{ link: PublicFileLink }>(`/files/${fileId}/public-links/${linkId}/regenerate`, { method: 'POST' }, orgId),
    disableAllPublicLinksForFile: (orgId: string, fileId: string) =>
      request<{ ok: boolean }>(`/files/${fileId}/public-links/disable-all`, { method: 'POST' }, orgId),

    listMyPublicSharedFiles: (orgId: string) =>
      request<{ items: PublicSharedFileItem[] }>('/shares/public/files', {}, orgId),
    listMyPublicAccessLogs: (orgId: string) =>
      request<{ logs: PublicFileAccessLog[] }>('/shares/public/access-logs', {}, orgId),
    listPublicAccessLogsForFile: (orgId: string, fileId: string) =>
      request<{ logs: PublicFileAccessLog[] }>(`/files/${fileId}/public-links/access-logs`, {}, orgId),
  },

  audit: {
    list: (orgId: string, params?: { limit?: number; offset?: number; actor_id?: string; resource_type?: string; resource_id?: string; action?: string }) => {
      const searchParams = new URLSearchParams()
      if (params?.limit) searchParams.set('limit', String(params.limit))
      if (params?.offset) searchParams.set('offset', String(params.offset))
      if (params?.actor_id) searchParams.set('actor_id', params.actor_id)
      if (params?.resource_type) searchParams.set('resource_type', params.resource_type)
      if (params?.resource_id) searchParams.set('resource_id', params.resource_id)
      if (params?.action) searchParams.set('action', params.action)
      return request<{ events: AuditEvent[]; total: number; limit: number; offset: number }>(`/audit?${searchParams}`, {}, orgId)
    },
    resource: (orgId: string, resourceType: string, resourceId: string, limit?: number) =>
      request<{ events: AuditEvent[] }>(`/audit/resource?resource_type=${resourceType}&resource_id=${resourceId}${limit ? `&limit=${limit}` : ''}`, {}, orgId),
  },
}

export interface User {
  id: string
  is_admin?: boolean
  email: string
  display_name: string
  avatar_url?: string
  created_at: string
  updated_at?: string
}

export interface PublicDropLink {
  id: string
  user_id: string
  org_id: string
  target_folder_id: string
  token: string
  label?: string
  enabled: boolean
  last_used_at?: string
  created_at: string
  updated_at: string
}

export interface PublicDropInfo {
  title: string
  display_name: string
  label?: string
  enabled: boolean
}

export interface Org {
  id: string
  name: string
  slug: string
  role?: string
  created_at: string
  updated_at?: string
}

export interface OrgMember {
  id: string
  user_id: string
  email?: string
  display_name?: string
  role: string
  created_at: string
}

export interface Team {
  id: string
  org_id: string
  name: string
  slug: string
  description?: string
  created_at: string
  updated_at?: string
  members?: TeamMember[]
}

export interface TeamMember {
  id: string
  user_id: string
  email?: string
  display_name?: string
  role: string
  created_at: string
}

export interface Folder {
  id: string
  org_id: string
  parent_id?: string
  owner_type: 'user' | 'team'
  owner_id: string
  name: string
  path: string
  created_by: string
  created_at: string
  updated_at: string
}

export interface FileRecord {
  id: string
  org_id: string
  folder_id: string
  owner_type: 'user' | 'team'
  owner_id: string
  name: string
  description?: string
  current_version_id?: string
  created_by: string
  created_at: string
  updated_at: string
}

export interface FileVersion {
  id: string
  file_id: string
  version_number: number
  storage_key: string
  size: number
  stored_size?: number
  compression_alg?: string
  checksum: string
  mime_type: string
  created_by: string
  created_at: string
  status: string
}

export interface FileWithVersion extends FileRecord {
  current_version?: FileVersion
  tags?: Tag[]
}

export interface Tag {
  id: string
  org_id?: string
  name: string
  color?: string
  created_at?: string
}

export interface ACLEntry {
  id: string
  org_id: string
  resource_type: string
  resource_id: string
  target_type: string
  target_id: string
  target_name?: string
  target_email?: string
  permissions: number
  inherited: boolean
  created_at: string
}

export interface AuditEvent {
  id: string
  org_id: string
  actor_id: string
  actor_name?: string
  action: string
  resource_type: string
  resource_id: string
  details: string
  ip_address?: string
  user_agent?: string
  created_at: string
}

export type SharePermission = 'view' | 'edit'

export interface Share {
  id: string
  org_id: string
  file_id?: string
  folder_id?: string
  target_user_id?: string
  target_team_id?: string
  permission: SharePermission
  created_by: string
  created_at: string
  updated_at: string
}

export interface ShareWithDetails extends Share {
  target_user?: User
  target_team?: Team
}

export interface ShareStatus {
  is_shared: boolean
  is_owner: boolean
  user_permission?: SharePermission
}

export interface SharedItem {
  share: Share
  file?: FileRecord
  folder?: Folder
}

// A file someone sent the current user through one of their public drop links.
export interface DroppedItem {
  file: FileRecord
  label?: string
  received_at: string
}
